import React, { useState, useEffect } from "react";
import "../pages/FertilizerScannerPage.css";

const FertilizerScannerPage = () => {
  const [images, setImages] = useState([]); // Array for multiple images
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]); // Array for multiple files
  const [activeTab, setActiveTab] = useState('actions'); // For tabbed interface
  const [imageQuality, setImageQuality] = useState(null); // Track image quality feedback
  const [currentImageIndex, setCurrentImageIndex] = useState(0); // For viewing multiple results
  const [processingProgress, setProcessingProgress] = useState({ current: 0, total: 0 }); // Progress tracking

  // Cleanup function to revoke object URLs
  useEffect(() => {
    return () => {
      images.forEach(img => {
        if (img.url) {
          URL.revokeObjectURL(img.url);
        }
      });
    };
  }, [images]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validate files
    const validFiles = [];
    const errors = [];

    files.forEach((file, index) => {
      if (file.size > 10 * 1024 * 1024) {
        errors.push(`Image ${index + 1} is too large (max 10MB)`);
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        errors.push(`File ${index + 1} is not a valid image`);
        return;
      }
      
      validFiles.push(file);
    });

    if (errors.length > 0) {
      setError(errors.join(', '));
      return;
    }

    // Limit to 5 images max
    if (validFiles.length > 5) {
      setError("Maximum 5 images allowed at once");
      return;
    }

    setSelectedFiles(validFiles);
    
    // Create preview URLs
    const imageUrls = validFiles.map(file => ({
      file,
      url: URL.createObjectURL(file),
      name: file.name
    }));
    
    setImages(imageUrls);
    setPredictions([]);
    setError(null);
    setImageQuality(null);
    setCurrentImageIndex(0);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError("Please select at least one image to upload.");
      return;
    }

    setLoading(true);
    setError(null);
    setPredictions([]);
    setProcessingProgress({ current: 0, total: selectedFiles.length });

    try {
      if (selectedFiles.length === 1) {
        // Single image - use existing endpoint
        setProcessingProgress({ current: 1, total: 1 });
        const formData = new FormData();
        formData.append("image", selectedFiles[0]);

        const response = await fetch(`${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001"))}/api/predict`, {
          method: "POST",
          body: formData,
        });

        const data = await response.json();
        
        if (response.ok && data.results && data.results.length > 0) {
          setPredictions([{
            imageIndex: 0,
            imageName: selectedFiles[0].name,
            imageUrl: images[0].url,
            results: data.results,
            metadata: data.metadata,
            confidence_level: data.confidence_level,
            detection_method: data.detection_method
          }]);
          
          if (data.metadata) {
            setImageQuality({
              method: data.detection_method,
              confidence: data.confidence_level,
              preprocessing: data.metadata.preprocessing
            });
          }
        } else {
          setPredictions([{
            imageIndex: 0,
            imageName: selectedFiles[0].name,
            imageUrl: images[0].url,
            error: data.error || "No disease detected in this image",
            suggestion: data.suggestion,
            possible_diseases: data.possible_diseases
          }]);
        }
      } else {
        // Multiple images - use new endpoint
        const formData = new FormData();
        selectedFiles.forEach((file, index) => {
          formData.append("images", file);
        });

        const response = await fetch(`${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001"))}/api/predict-multiple`, {
          method: "POST",
          body: formData,
        });

        const data = await response.json();
        
        if (response.ok && data.results) {
          const processedResults = data.results.map((result, index) => ({
            imageIndex: result.index,
            imageName: result.filename,
            imageUrl: images[result.index].url,
            ...(result.success ? {
              results: result.results,
              metadata: result.metadata,
              confidence_level: result.confidence_level,
              detection_method: result.detection_method
            } : {
              error: result.error,
              suggestion: result.suggestion,
              possible_diseases: result.possible_diseases
            })
          }));

          setPredictions(processedResults);
          
          // Set image quality for the first successful result
          const firstSuccess = processedResults.find(r => r.results && r.results.length > 0);
          if (firstSuccess && firstSuccess.metadata) {
            setImageQuality({
              method: firstSuccess.detection_method,
              confidence: firstSuccess.confidence_level,
              preprocessing: firstSuccess.metadata.preprocessing
            });
          }
        } else {
          setError(data.error || "Failed to process images");
        }
      }

    } catch (err) {
      setError("An error occurred while processing images. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fertilizer-scanner-container">
      {/* HEADER */}
      <div className="header-section">
        <h1 className="header-title">
          🌱 Smart Crop Disease Detector
        </h1>
        <p className="header-subtitle">
          Detect plant diseases instantly using <strong>AI-powered leaf analysis</strong>. 
          Get <strong>disease identification</strong>, <strong>treatment options</strong>, 
          and <strong>prevention methods</strong> for better crop health.
        </p>
      </div>

      {/* MAIN CONTENT */}
      <div className="main-content">
        {/* UPLOAD SECTION */}
        <div className="upload-section">
          <h2 className="upload-title">Upload and Scan Your Crop Leaves</h2>
          <p className="upload-subtitle">Select one or multiple clear images of your crop leaves to detect diseases</p>
          
          {/* Image Tips */}
          <div className="image-tips">
            <h4>📸 For Best Results:</h4>
            <ul>
              <li>✓ Take photo in good natural lighting</li>
              <li>✓ Focus on the diseased leaf area</li>
              <li>✓ Ensure leaf fills most of the frame</li>
              <li>✓ Avoid blurry or dark images</li>
              <li>✓ Show clear disease symptoms</li>
            </ul>
          </div>
          
          <div className="upload-zone">
            <input
              type="file"
              accept="image/*"
              className="file-input"
              id="image-upload"
              multiple
              onChange={handleImageChange}
            />
            <label htmlFor="image-upload" className="upload-button">
              🖼️ Choose Images (Max 5)
            </label>
            <p className="upload-hint">
              Select single or multiple images • Hold Ctrl/Cmd to select multiple
            </p>
          </div>

          {images.length > 0 && (
            <div className="fade-in">
              {/* Automatically show appropriate preview based on image count */}
              {images.length > 1 ? (
                <div className="multiple-images-preview">
                  <h4 className="preview-title">Selected Images ({images.length})</h4>
                  <div className="images-grid">
                    {images.map((img, index) => (
                      <div key={index} className="image-thumbnail">
                        <img
                          src={img.url}
                          alt={`Preview ${index + 1}`}
                          className="thumbnail-image"
                        />
                        <div className="thumbnail-info">
                          <span className="image-number">{index + 1}</span>
                          <span className="image-name">{img.name.substring(0, 15)}...</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                /* Single Image Preview */
                <div className="image-preview-container">
                  <div className="preview-wrapper">
                    <img
                      src={images[0].url}
                      alt="Leaf Preview"
                      className="leaf-image"
                    />
                  </div>
                  {imageQuality && (
                    <div className={`quality-badge ${imageQuality.confidence}`}>
                      {imageQuality.confidence === 'high' && '✓ High Confidence'}
                      {imageQuality.confidence === 'medium' && '⚠ Medium Confidence'}
                      {imageQuality.confidence === 'low' && '⚠ Low Confidence - Verify'}
                    </div>
                  )}
                </div>
              )}

              <button
                className="upload-button analyze-btn"
                onClick={handleUpload}
                disabled={loading}
              >
                {loading ? "🔍 Analyzing..." : `Analyze ${images.length > 1 ? `${images.length} Images` : 'Image'}`}
              </button>
            </div>
          )}

          {loading && (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p className="loading-text">
                {processingProgress.total > 1 
                  ? `Processing images... ${processingProgress.current}/${processingProgress.total}`
                  : "Processing image... Please wait"
                }
              </p>
              {processingProgress.total > 1 && (
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${(processingProgress.current / processingProgress.total) * 100}%` }}
                  ></div>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="error-container">
              <div className="error-icon">⚠️</div>
              <div className="error-message" style={{ whiteSpace: 'pre-line' }}>
                {error}
              </div>
            </div>
          )}
        </div>

        {/* RESULTS SECTION - REDESIGNED FOR FARMERS */}
        {predictions.length > 0 && (
          <div className="results-section slide-up">
            <h2 className="results-title">🔍 Disease Detection Results</h2>
            
            {/* Multiple Results Navigation */}
            {predictions.length > 1 && (
              <div className="results-navigation">
                <div className="nav-header">
                  <h3>Analysis Results ({predictions.length} images)</h3>
                  <div className="results-summary">
                    {(() => {
                      const successful = predictions.filter(p => p.results && p.results.length > 0).length;
                      const failed = predictions.length - successful;
                      return (
                        <span>
                          ✅ {successful} successful • {failed > 0 ? `❌ ${failed} failed` : ''}
                        </span>
                      );
                    })()}
                  </div>
                </div>
                
                <div className="image-tabs">
                  {predictions.map((pred, index) => (
                    <button
                      key={index}
                      className={`image-tab ${currentImageIndex === index ? 'active' : ''} ${pred.error ? 'error' : 'success'}`}
                      onClick={() => setCurrentImageIndex(index)}
                    >
                      <img src={pred.imageUrl} alt={`Result ${index + 1}`} className="tab-thumbnail" />
                      <div className="tab-info">
                        <span className="tab-number">#{index + 1}</span>
                        <span className="tab-status">
                          {pred.error ? '❌' : '✅'}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Analysis Quality Info */}
            {/* {imageQuality && (
              <div className={`analysis-info ${imageQuality.confidence === 'very_low' ? 'low' : imageQuality.confidence}`}>
                <span className="info-icon">
                  {imageQuality.confidence === 'high' ? '✓' : '⚠'}
                </span>
                <div className="info-text">
                  <strong>Analysis Quality: {imageQuality.confidence === 'very_low' ? 'VERY LOW' : imageQuality.confidence.toUpperCase()}</strong>
                  <p>
                    {imageQuality.confidence === 'high' && 'Multiple AI models agree on this diagnosis with high confidence.'}
                    {imageQuality.confidence === 'medium' && 'Good detection, but consider consulting an expert for confirmation.'}
                    {imageQuality.confidence === 'low' && 'Low confidence detection. Please verify with an agricultural expert.'}
                    {imageQuality.confidence === 'very_low' && '⚠️ VERY LOW CONFIDENCE - These are educated guesses based on limited information. Please retake the photo with better lighting and focus, or consult an agricultural expert for accurate diagnosis.'}
                  </p>
                </div>
              </div>
            )} */}
            
            {(() => {
              const currentPred = predictions[currentImageIndex] || predictions[0];
              
              // Handle error cases
              if (currentPred.error) {
                return (
                  <div className="error-result-card fade-in">
                    <div className="error-header">
                      <img src={currentPred.imageUrl} alt="Failed analysis" className="error-image" />
                      <div className="error-info">
                        <h3>❌ Analysis Failed</h3>
                        <p className="error-filename">{currentPred.imageName}</p>
                      </div>
                    </div>
                    <div className="error-content">
                      <p className="error-message">{currentPred.error}</p>
                      {currentPred.suggestion && (
                        <div className="error-suggestion">
                          <h4>💡 Suggestion:</h4>
                          <p>{currentPred.suggestion}</p>
                        </div>
                      )}
                      {currentPred.possible_diseases && currentPred.possible_diseases.length > 0 && (
                        <div className="possible-diseases">
                          <h4>📊 Possible diseases (low confidence):</h4>
                          <ul>
                            {currentPred.possible_diseases.map((disease, i) => (
                              <li key={i}>{disease.disease}: {disease.confidence}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                );
              }

              const pred = currentPred.results[0]; // Show only top prediction for current image
              const info = pred.info || pred.treatment_info || {}; // Use info first (has extracted sections)
              const isVeryLowConfidence = pred.confidence_score < 0.25;

              return (
                <div className="disease-result-card fade-in">
                  {/* Very Low Confidence Warning */}
                  {isVeryLowConfidence && (
                    <div className="very-low-confidence-warning">
                      <div className="warning-icon">⚠️</div>
                      <div className="warning-content">
                        <h4>Very Low Confidence Detection</h4>
                        <p>The AI models are uncertain about this diagnosis. This could be because:</p>
                        <ul>
                          <li>Image quality is poor (blurry, dark, or overexposed)</li>
                          <li>Disease symptoms are not clearly visible</li>
                          <li>The disease is not in the training database</li>
                          <li>Multiple diseases are present</li>
                        </ul>
                        <p><strong>Recommendation:</strong> Retake the photo with better lighting and focus on the diseased area, or consult an agricultural expert.</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Disease Header with Image */}
                  <div className="result-header">
                    <div className="result-image-container">
                      <img 
                        src={currentPred.imageUrl} 
                        alt="Analyzed leaf" 
                        className="result-image"
                      />
                      <div className="image-label">
                        {predictions.length > 1 && (
                          <span className="image-counter">Image {currentImageIndex + 1} of {predictions.length}</span>
                        )}
                        <span className="filename">{currentPred.imageName}</span>
                      </div>
                    </div>
                    <div className="disease-info">
                      <span className="disease-icon">🦠</span>
                      <div>
                        <h3 className="disease-title">{pred.disease || pred.full_label}</h3>
                        <p className="crop-name">Crop: {pred.crop}</p>
                        <span className={`confidence-indicator ${
                          pred.confidence_score >= 0.7 ? 'high' : pred.confidence_score >= 0.35 ? 'medium' : 'low'
                        }`}>
                          {pred.confidence} Match
                          {pred.confidence_score < 0.7 && ' - Expert verification recommended'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* What is this Disease */}
                  {info.description && info.description !== "Not available" && (
                    <div className="info-section">
                      <div className="section-header">
                        <span className="section-icon">📋</span>
                        <h4>What is this Disease?</h4>
                      </div>
                      <p className="section-content">
                        {info.description}
                      </p>
                    </div>
                  )}

                  {/* Tabbed Interface for Better Organization */}
                  <div className="info-tabs">
                    <div className="tab-buttons">
                      <button 
                        className={`tab-btn ${activeTab === 'actions' ? 'active' : ''}`}
                        onClick={() => setActiveTab('actions')}
                      >
                        🚨 Actions
                      </button>
                      <button 
                        className={`tab-btn ${activeTab === 'treatment' ? 'active' : ''}`}
                        onClick={() => setActiveTab('treatment')}
                      >
                        💊 Treatment
                      </button>
                      <button 
                        className={`tab-btn ${activeTab === 'prevention' ? 'active' : ''}`}
                        onClick={() => setActiveTab('prevention')}
                      >
                        🛡️ Prevention
                      </button>
                      <button 
                        className={`tab-btn ${activeTab === 'help' ? 'active' : ''}`}
                        onClick={() => setActiveTab('help')}
                      >
                        ⚠️ Get Help
                      </button>
                    </div>

                    <div className="tab-content">
                      {/* Actions Tab */}
                      {activeTab === 'actions' && (
                        <div className="tab-panel fade-in">
                          {info.why_happened && info.why_happened !== "Not available" && (
                            <div className="compact-section">
                              <h5 className="compact-title">❓ Why This Happened:</h5>
                              <ul className="compact-list">
                                {info.why_happened.split('\n').filter(l => l.trim()).map((line, i) => (
                                  <li key={i}>{line}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {info.immediate_actions && info.immediate_actions !== "Not available" && (
                            <div className="compact-section urgent-box">
                              <h5 className="compact-title">🚨 Do This Now (24-48 Hours):</h5>
                              <ul className="action-list">
                                {info.immediate_actions.split('\n').filter(l => l.trim()).map((line, i) => (
                                  <li key={i}>{line}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Treatment Tab */}
                      {activeTab === 'treatment' && info.solutions && info.solutions !== "Not available" && (
                        <div className="tab-panel fade-in">
                          <div className="treatment-grid">
                            {(() => {
                              const lines = info.solutions.split('\n').filter(l => l.trim());
                              const organicStart = lines.findIndex(l => l.toLowerCase().includes('organic'));
                              const chemicalStart = lines.findIndex(l => l.toLowerCase().includes('chemical'));
                              
                              return (
                                <>
                                  {organicStart !== -1 && (
                                    <div className="treatment-card organic">
                                      <h5>🌿 Organic/Home Remedies</h5>
                                      <ul>
                                        {lines.slice(organicStart + 1, chemicalStart !== -1 ? chemicalStart : undefined)
                                          .filter(l => l.trim() && !l.toLowerCase().includes('chemical'))
                                          .map((line, i) => (
                                            <li key={i}>{line}</li>
                                          ))}
                                      </ul>
                                    </div>
                                  )}
                                  
                                  {chemicalStart !== -1 && (
                                    <div className="treatment-card chemical">
                                      <h5>⚗️ Chemical Treatments</h5>
                                      <ul>
                                        {lines.slice(chemicalStart + 1)
                                          .filter(l => l.trim())
                                          .map((line, i) => (
                                            <li key={i}>{line}</li>
                                          ))}
                                      </ul>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      )}

                      {/* Prevention Tab */}
                      {activeTab === 'prevention' && info.preventions && info.preventions !== "Not available" && (
                        <div className="tab-panel fade-in">
                          <div className="prevention-box">
                            <h5>🛡️ Prevent This in Future:</h5>
                            <ul className="prevention-list">
                              {info.preventions.split('\n').filter(l => l.trim()).map((line, i) => (
                                <li key={i}>{line}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}

                      {/* Help Tab */}
                      {activeTab === 'help' && info.when_seek_help && info.when_seek_help !== "Not available" && (
                        <div className="tab-panel fade-in">
                          <div className="help-box">
                            <h5>⚠️ Seek Expert Help If:</h5>
                            <ul className="help-list">
                              {info.when_seek_help.split(/[.!]\s+/).filter(l => l.trim()).map((line, i) => (
                                <li key={i}>{line}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Other Possible Diseases for Current Image */}
                  {currentPred.results && currentPred.results.length > 1 && (
                    <div className="info-section alternative">
                      <div className="section-header">
                        <span className="section-icon">🔄</span>
                        <h4>Other Possible Diseases (This Image)</h4>
                      </div>
                      <div className="alternatives-list">
                        {currentPred.results.slice(1, 3).map((altPred, i) => (
                          <div key={i} className="alternative-item">
                            <span className="alt-name">{altPred.disease || altPred.full_label}</span>
                            <span className="alt-crop">({altPred.crop})</span>
                            <span className="alt-confidence">{altPred.confidence}</span>
                          </div>
                        ))}
                      </div>
                      <p className="alternative-note">
                        If symptoms don't match the top result, consider these alternatives.
                      </p>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default FertilizerScannerPage;